import {
  ApiCustomFieldValue,
  ApiField,
  ApiFieldTypes,
  ApiFieldValueTypes,
  ApiTableTypes,
} from "@/types/custom-field.types";
import { ApiHiringFlow, ApiHiringFlowSection } from "@/types/hiring-flow.types";
import { ApiEmployeeEditPayload } from "@/types/employees/employee.types";
import { EmployeeCompensationFormValues } from "@/pages/employees/employee/components/employee-compensation-form.types";
import {
  ApiEmployeeHirePackage,
  ApiEmployeeHirePayload,
  ApiEmployeeHireWorkflow,
} from "@/types/employees/employee-hire.types";

export type HireFieldValues = Record<string, unknown>;
export interface HirePackageState {
  values: EmployeeCompensationFormValues | null;
  payload: HireLinesPayload | null;
}
export type HireLinesPayload = Pick<ApiEmployeeHirePackage, "currency" | "lines">;
export const emptyHirePackage: HirePackageState = { values: null, payload: null };
export interface HireOnboardingValues {
  enable_probation: boolean;
  probation_policy_id: number | null;
  date_probation_ends: Date | null;
  compensation_enabled: boolean;
  compensation: HirePackageState;
  compensation_columns: HireFieldValues;
  workflows_enabled: boolean;
  workflows: ApiEmployeeHireWorkflow[];
}
export const mainFieldNames: Partial<Record<ApiFieldTypes, keyof ApiEmployeeHirePayload["main"]>> =
  {
    [ApiFieldTypes.employee_first_name]: "first_name",
    [ApiFieldTypes.employee_last_name]: "last_name",
    [ApiFieldTypes.employee_middle_name]: "middle_name",
    [ApiFieldTypes.employee_position_division]: "division_id",
    [ApiFieldTypes.employee_position_department]: "department_id",
    [ApiFieldTypes.employee_position_job_profile]: "job_profile_id",
    [ApiFieldTypes.employee_position_location]: "location_id",
    [ApiFieldTypes.employee_position_reporting_to]: "reporting_to_id",
    [ApiFieldTypes.employee_employment_legal_entity]: "legal_entity_id",
    [ApiFieldTypes.employee_employment_contract]: "employment_contract_id",
    [ApiFieldTypes.employee_employment_type]: "employment_type_id",
    [ApiFieldTypes.employee_employment_work_pattern]: "employment_work_pattern_id",
  };
export const personalFieldNames: Partial<Record<ApiFieldTypes, keyof ApiEmployeeEditPayload>> = {
  [ApiFieldTypes.employee_email]: "email",
  [ApiFieldTypes.employee_email_personal]: "email_personal",
  [ApiFieldTypes.employee_phone]: "phone",
  [ApiFieldTypes.employee_phone_personal]: "phone_personal",
  [ApiFieldTypes.employee_number]: "personnel_number",
  [ApiFieldTypes.employee_date_birth]: "date_birth",
  [ApiFieldTypes.employee_gender]: "gender",
  [ApiFieldTypes.employee_address_address_line_1]: "address_1",
  [ApiFieldTypes.employee_address_address_line_2]: "address_2",
  [ApiFieldTypes.employee_address_city]: "city",
  [ApiFieldTypes.employee_address_state]: "state",
  [ApiFieldTypes.employee_address_zip_code]: "zip",
  [ApiFieldTypes.employee_address_country]: "country_id",
  [ApiFieldTypes.employee_name_pronunciation]: "name_pronunciation",
};

export function getHireSections(sections: ApiHiringFlowSection[]) {
  return sections
    .map((section) => ({
      ...section,
      fields: section.fields
        .filter((field) => field.is_enabled)
        .map((item): ApiField => ({
          ...item.field,
          section_id: 0,
          table_id: 0,
          is_archived: false,
          is_hidden: false,
          is_mandatory: item.is_mandatory,
        })),
    }))
    .filter((section) => section.fields.length > 0);
}

export function getHireStepSections(
  flow: ApiHiringFlow,
  group: "main" | "personal",
  personnelNumberAuto: boolean | undefined
) {
  return getHireSections(flow.form[group])
    .map((section) => ({
      ...section,
      fields: section.fields.filter((field) => {
        if (
          group === "main" &&
          [
            ApiFieldTypes.employee_first_name,
            ApiFieldTypes.employee_last_name,
            ApiFieldTypes.employee_middle_name,
          ].includes(field.type)
        )
          return false;
        return field.type !== ApiFieldTypes.employee_number || !personnelNumberAuto;
      }),
    }))
    .filter((section) => section.fields.length > 0);
}

export function isHirePackageEmpty(values: EmployeeCompensationFormValues) {
  return values.lines.every(
    (line) =>
      (line.amount_gross === null || line.amount_gross === "") &&
      (line.amount_net === null || line.amount_net === "")
  );
}

const compensationCustomPrefix = `table_${ApiTableTypes.employee_compensations}-`;

export function isHireCompensationColumn(field: ApiField) {
  return (
    field.type === ApiFieldTypes.employee_compensation_lines ||
    field.type === ApiFieldTypes.employee_compensation_change_reason ||
    field.type === ApiFieldTypes.employee_compensation_comment ||
    (field.type === ApiFieldTypes.custom && field.slug.startsWith(compensationCustomPrefix))
  );
}

function hasCustomValue(value: ApiCustomFieldValue) {
  return Object.values(value).some(
    (item) =>
      item !== null && item !== undefined && item !== "" && !(Array.isArray(item) && !item.length)
  );
}

export function getHirePackageColumns(fields: ApiField[]) {
  return fields.filter(isHireCompensationColumn);
}

function toHireFieldValue(field: ApiField, values: HireFieldValues): ApiCustomFieldValue {
  return {
    ...toHireCustomValue(field, values[field.slug], values[`${field.slug}_label`]),
    ...(field.field_type === ApiFieldValueTypes.currency
      ? {
          value_text: values[`${field.slug}_currency`]
            ? String(values[`${field.slug}_currency`])
            : null,
        }
      : {}),
  };
}

function toScalar(raw: unknown) {
  const scalar = Array.isArray(raw) ? (raw[0] ?? null) : (raw ?? null);
  return scalar === "" ? null : scalar;
}

export function buildHirePackage(
  columns: ApiField[],
  values: HireFieldValues,
  lines: HireLinesPayload | null
): ApiEmployeeHirePackage | null {
  const result: ApiEmployeeHirePackage = {
    currency: lines?.currency ?? null,
    lines: lines?.lines ?? [],
    change_reason_id: null,
    comment: null,
    custom_fields: {},
  };
  for (const field of columns) {
    const scalar = toScalar(values[field.slug]);
    if (field.type === ApiFieldTypes.employee_compensation_change_reason)
      result.change_reason_id = scalar === null ? null : Number(scalar);
    else if (field.type === ApiFieldTypes.employee_compensation_comment)
      result.comment = scalar === null ? null : String(scalar).trim() || null;
    else if (field.type === ApiFieldTypes.custom)
      result.custom_fields[field.slug] = toHireFieldValue(field, values);
  }
  const hasColumnValues =
    result.change_reason_id !== null ||
    result.comment !== null ||
    Object.values(result.custom_fields).some((value) => value !== null && hasCustomValue(value));
  return lines || hasColumnValues ? result : null;
}

export function isHireCompensationRequired(flow: ApiHiringFlow) {
  return flow.form.main.some((section) =>
    section.fields.some(
      (item) =>
        item.field.type === ApiFieldTypes.employee_compensation_lines &&
        item.is_enabled &&
        item.is_mandatory
    )
  );
}

export function getHireOnboardingDefaults(flow: ApiHiringFlow): HireOnboardingValues {
  return {
    enable_probation: flow.probation_is_enabled && flow.probation_is_mandatory,
    probation_policy_id: flow.probation_policy_id,
    date_probation_ends: null,
    compensation_enabled:
      flow.compensation_after_probation_is_enabled &&
      flow.compensation_after_probation_is_mandatory,
    compensation: emptyHirePackage,
    compensation_columns: {},
    workflows_enabled:
      flow.workflows_is_enabled && (flow.workflows_is_mandatory || flow.workflow_ids.length > 0),
    workflows: flow.workflows_is_enabled
      ? flow.workflow_ids.map((workflow_id) => ({ workflow_id, block_portal_access: false }))
      : [],
  };
}

export function toHireCustomValue(
  field: ApiField,
  raw: unknown,
  label?: unknown
): ApiCustomFieldValue {
  switch (field.field_type) {
    case ApiFieldValueTypes.url:
      return {
        value_text: raw == null || raw === "" ? null : String(raw),
        value_label: label == null || label === "" ? null : String(label),
      };
    case ApiFieldValueTypes.number:
      return { value_int: raw === "" || raw == null ? null : Number(raw) };
    case ApiFieldValueTypes.currency:
    case ApiFieldValueTypes.percentage:
      return { value_float: raw === "" || raw == null ? null : Number(raw) };
    case ApiFieldValueTypes.switch:
      return { value_bool: raw == null ? null : Boolean(raw) };
    case ApiFieldValueTypes.select:
    case ApiFieldValueTypes.employee_reference:
    case ApiFieldValueTypes.entity_reference:
      return { value_int_array: Array.isArray(raw) ? raw.map(Number) : [] };
    case ApiFieldValueTypes.text_array:
      return { value_text_array: Array.isArray(raw) ? raw.map(String) : [] };
    case ApiFieldValueTypes.date:
      return { date_value: raw instanceof Date ? raw : null };
    case ApiFieldValueTypes.datetime:
      return { datetime_value: raw instanceof Date ? raw : null };
    default:
      return { value_text: raw == null || raw === "" ? null : String(raw) };
  }
}

export function buildHirePayload(
  flow: ApiHiringFlow,
  mainValues: HireFieldValues,
  personalValues: HireFieldValues,
  onboarding: HireOnboardingValues,
  candidateId?: number,
  compensation: HirePackageState = emptyHirePackage,
  afterProbationColumns: ApiField[] = []
): ApiEmployeeHirePayload {
  const main: ApiEmployeeHirePayload["main"] = {
    first_name: String(mainValues.first_name ?? "").trim(),
    last_name: String(mainValues.last_name ?? "").trim(),
    middle_name: mainValues.middle_name ? String(mainValues.middle_name).trim() : null,
    avatar_file_uuid: mainValues.avatar_file_uuid ? String(mainValues.avatar_file_uuid) : null,
    date_hired_on: mainValues.date_hired_on as Date,
    candidate_id: candidateId ?? null,
    division_id: null,
    department_id: null,
    job_profile_id: null,
    location_id: null,
    legal_entity_id: null,
    reporting_to_id: null,
    employment_contract_id: null,
    employment_type_id: null,
    employment_work_pattern_id: null,
    custom_fields: {},
  };
  const personal: ApiEmployeeHirePayload["personal"] = { custom_fields: {} };
  const compensationColumns: ApiField[] = [];
  for (const group of ["main", "personal"] as const) {
    const values = group === "main" ? mainValues : personalValues;
    const target = group === "main" ? main : personal;
    for (const section of getHireSections(flow.form[group])) {
      for (const field of section.fields) {
        if (group === "main" && isHireCompensationColumn(field)) {
          compensationColumns.push(field);
        } else if (field.type === ApiFieldTypes.custom) {
          target.custom_fields![field.slug] = toHireFieldValue(field, values);
        } else {
          const name = (group === "main" ? mainFieldNames : personalFieldNames)[field.type];
          if (!name || ["first_name", "last_name", "middle_name"].includes(name)) continue;
          (target as Record<string, unknown>)[name] = toScalar(values[field.slug]);
        }
      }
    }
  }
  return {
    hiring_flow_id: flow.id,
    main,
    personal,
    compensation: compensationColumns.length
      ? buildHirePackage(compensationColumns, mainValues, compensation.payload)
      : null,
    enable_probation: flow.probation_is_enabled && onboarding.enable_probation,
    date_probation_ends:
      flow.probation_is_enabled && onboarding.enable_probation
        ? onboarding.date_probation_ends
        : null,
    compensation_after_probation:
      flow.probation_is_enabled &&
      onboarding.enable_probation &&
      flow.compensation_after_probation_is_enabled &&
      onboarding.compensation_enabled
        ? buildHirePackage(
            afterProbationColumns,
            onboarding.compensation_columns,
            onboarding.compensation.payload
          )
        : null,
    workflows:
      flow.workflows_is_enabled && onboarding.workflows_enabled ? onboarding.workflows : [],
  };
}
